/**
 * Admin Console v2 — purpose-built admin pages mounted at /dashboard/admin/*.
 * The DashboardLayout swaps to ADMIN_NAV when user.role === "admin".
 *
 * Pages exported:
 *   AdminOverviewPage      — KPIs (users, weddings, MRR, moderation queue depth)
 *   AdminWeddingsPage      — search, suspend/unsuspend, view-as
 *   AdminUsersPage         — set plan, view (delegated)
 *   AdminModerationPage    — pending memories, blessings, vendors, announcements
 *   AdminVendorsPage       — vendor approval queue (subset of moderation)
 *   AdminRevenuePage       — Stripe txn summary + daily series + top customers
 *   AdminReportsPage       — flagged content from /report
 *   AdminBroadcastPage     — send platform-wide email/SMS via Resend / Twilio
 *   AdminAuditLogPage      — admin actions trail
 */
import React, { useEffect, useMemo, useState } from "react";
import { WeddingJourney } from "../../components/dashboard/WeddingJourney";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, Building, ShieldCheck, Store, TrendingUp, FlagTriangleRight, Send,
  ScrollText, Megaphone, AlertCircle, Check, X, Search, ExternalLink, RefreshCw,
  DollarSign, Activity, Image as ImageIcon, Mic, FileText, Trash2, ChevronRight,
} from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import { useAuth } from "../../lib/auth";

// ---------- shared ----------
function AdminGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-8">
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Admin</p>
        <h1 className="font-serif font-light text-4xl">{title}</h1>
        {subtitle && <p className="text-[var(--muted)] mt-2 text-sm">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function Card({ children, className = "", ...rest }) {
  return (
    <div className={`bg-white border border-[var(--line)] rounded-2xl p-5 ${className}`} {...rest}>
      {children}
    </div>
  );
}

function Stat({ label, value, sub, icon: Icon, accent = "gold", testid }) {
  return (
    <Card data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted)]">{label}</p>
        {Icon && (
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            accent === "red" ? "bg-red-50 text-red-500"
            : accent === "green" ? "bg-emerald-50 text-emerald-600"
            : "bg-[var(--bg-warm)] text-[var(--gold)]"
          }`}>
            <Icon className="w-4 h-4" />
          </div>
        )}
      </div>
      <p className="font-serif font-light text-3xl leading-none">{value}</p>
      {sub && <p className="text-xs text-[var(--muted)] mt-2">{sub}</p>}
    </Card>
  );
}

// ===================== OVERVIEW =====================
export function AdminOverviewPage() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stats/overview");
      setS(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  return (
    <AdminGate>
      <div data-testid="admin-overview">
        <div className="mb-10" data-testid="admin-our-wedding">
          <p className="font-script text-[var(--gold)] text-3xl mb-3">Our wedding</p>
          <WeddingJourney />
        </div>
        <PageHeader
          title="Platform Overview"
          subtitle="Live stats across LockedInForever."
          action={
            <button onClick={load} disabled={loading} data-testid="admin-overview-refresh" className="btn-outline-gold text-xs">
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          }
        />
        {!s ? (
          <div className="text-center py-20"><div className="w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Stat testid="kpi-users-total" label="Total users" value={s.users.total} sub={`+${s.users.new_30d} in 30d`} icon={Users} />
              <Stat testid="kpi-weddings-total" label="Weddings" value={s.weddings.total} sub={`${s.weddings.new_30d} new in 30d · ${s.weddings.suspended} suspended`} icon={Building} />
              <Stat testid="kpi-revenue-total" label="Gross revenue (USD)" value={`$${(s.revenue.gross_total_usd || 0).toLocaleString()}`} sub={`$${(s.revenue.gross_30d_usd || 0).toLocaleString()} in last 30d · ${s.revenue.paid_txn_count} paid txns`} icon={DollarSign} accent="green" />
              <Stat testid="kpi-moderation-total" label="Moderation queue" value={s.moderation.total} sub={`${s.moderation.memories_pending} memories · ${s.moderation.blessings_pending} blessings · ${s.moderation.vendors_pending} vendors · ${s.moderation.reports_open} reports`} icon={ShieldCheck} accent={s.moderation.total > 0 ? "red" : "gold"} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              <Stat label="Couples" value={s.users.couples} icon={Users} />
              <Stat label="Vendors" value={s.users.vendors} sub={`${s.vendors.pending} pending`} icon={Store} accent={s.vendors.pending > 0 ? "red" : "gold"} />
              <Stat label="Planners" value={s.users.planners} icon={Building} />
              <Stat label="Live sessions" value={s.live.active_or_scheduled} sub="Active or scheduled" icon={Activity} />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <h3 className="font-serif text-xl mb-4">Quick actions</h3>
                <div className="grid grid-cols-2 gap-2">
                  <Link to="/dashboard/admin/moderation" className="btn-outline-gold text-xs justify-start" data-testid="quick-moderation"><ShieldCheck className="w-3 h-3" /> Moderate ({s.moderation.total})</Link>
                  <Link to="/dashboard/admin/weddings" className="btn-outline-gold text-xs justify-start" data-testid="quick-weddings"><Building className="w-3 h-3" /> Manage weddings</Link>
                  <Link to="/dashboard/admin/revenue" className="btn-outline-gold text-xs justify-start" data-testid="quick-revenue"><TrendingUp className="w-3 h-3" /> Revenue</Link>
                  <Link to="/dashboard/admin/broadcast" className="btn-outline-gold text-xs justify-start" data-testid="quick-broadcast"><Send className="w-3 h-3" /> Broadcast</Link>
                  <Link to="/admin" className="btn-outline-gold text-xs justify-start" data-testid="quick-studio"><FileText className="w-3 h-3" /> Studio (podcast / promos)</Link>
                  <Link to="/dashboard/admin/audit" className="btn-outline-gold text-xs justify-start" data-testid="quick-audit"><ScrollText className="w-3 h-3" /> Audit log</Link>
                </div>
              </Card>

              <Card>
                <h3 className="font-serif text-xl mb-4">User mix</h3>
                <Bar label="Couples" value={s.users.couples} total={s.users.total} />
                <Bar label="Vendors" value={s.users.vendors} total={s.users.total} />
                <Bar label="Planners" value={s.users.planners} total={s.users.total} />
                <Bar label="Admins" value={s.users.admins} total={s.users.total} />
              </Card>
            </div>
          </>
        )}
      </div>
    </AdminGate>
  );
}

function Bar({ label, value, total }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between text-xs mb-1">
        <span>{label}</span>
        <span className="text-[var(--muted)]">{value} · {pct}%</span>
      </div>
      <div className="h-1.5 bg-[var(--bg-warm)] rounded-full overflow-hidden">
        <div className="h-full bg-[var(--gold)]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ===================== WEDDINGS =====================
export function AdminWeddingsPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [showSuspended, setShowSuspended] = useState(false);

  async function load() {
    setBusy(true);
    try {
      const params = { limit: 200 };
      if (q.trim()) params.q = q.trim();
      if (showSuspended) params.suspended = true;
      const { data } = await api.get("/admin/weddings", { params });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }
  useEffect(() => { load(); }, [showSuspended]); // eslint-disable-line react-hooks/exhaustive-deps

  async function suspend(wid) {
    const reason = prompt("Suspension reason (visible only to admins):", "");
    if (reason === null) return;
    try {
      await api.post(`/admin/weddings/${wid}/suspend`, { reason });
      toast.success("Wedding suspended");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function unsuspend(wid) {
    try {
      await api.post(`/admin/weddings/${wid}/unsuspend`, {});
      toast.success("Wedding restored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function toggleFeed(w) {
    try {
      await api.post(`/admin/weddings/${w.wedding_id}/feature-feed`, {
        is_featured_on_feed: !w.is_featured_on_feed,
      });
      toast.success(!w.is_featured_on_feed ? "Featured on feed" : "Removed from featured");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <AdminGate>
      <div data-testid="admin-weddings">
        <PageHeader title="Weddings" subtitle="Search, view, and moderate every wedding on the platform." />

        <Card className="mb-6">
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by slug, couple names, or city…"
                data-testid="admin-weddings-search"
                className="input-luxe pl-10"
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-[var(--muted)] cursor-pointer select-none">
              <input type="checkbox" checked={showSuspended} onChange={(e) => setShowSuspended(e.target.checked)} data-testid="admin-weddings-show-suspended" />
              Suspended only
            </label>
            <button type="submit" data-testid="admin-weddings-search-btn" className="btn-gold">Search</button>
          </form>
        </Card>

        <Card>
          <p className="text-xs text-[var(--muted)] mb-3">{busy ? "Loading…" : `${items.length} weddings`}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-weddings-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] border-b border-[var(--line)]">
                  <th className="py-2 pr-4">Couple</th>
                  <th className="py-2 pr-4">Slug</th>
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Owner</th>
                  <th className="py-2 pr-4">Plan</th>
                  <th className="py-2 pr-4">Guests</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((w) => (
                  <tr key={w.wedding_id} className="border-b border-[var(--line)]/60 hover:bg-[#EDE3D3]/50">
                    <td className="py-3 pr-4 font-medium">{w.couple_names || "—"}</td>
                    <td className="py-3 pr-4 text-[var(--muted)] text-xs">{w.slug}</td>
                    <td className="py-3 pr-4 text-xs text-[var(--muted)]">{(w.wedding_date || "").slice(0, 10)}</td>
                    <td className="py-3 pr-4 text-xs">{w.owner_email || "—"}</td>
                    <td className="py-3 pr-4 text-xs uppercase">{w.owner_plan || "free"}</td>
                    <td className="py-3 pr-4 text-xs">{w.guests_count ?? 0}</td>
                    <td className="py-3 pr-4">
                      {w.is_suspended ? (
                        <span className="text-[10px] px-2 py-0.5 bg-red-50 text-red-600 rounded-full">SUSPENDED</span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full">LIVE</span>
                      )}
                    </td>
                    <td className="py-3 pr-2 text-right whitespace-nowrap">
                      <a href={`/site/${w.slug}`} target="_blank" rel="noreferrer" className="text-xs text-[var(--gold)] hover:underline mr-3" data-testid={`view-site-${w.slug}`}>
                        <ExternalLink className="w-3 h-3 inline" /> View
                      </a>
                      <button onClick={() => toggleFeed(w)} className={`text-xs mr-3 hover:underline ${w.is_featured_on_feed ? "text-[var(--gold)]" : "text-[var(--muted)]"}`} data-testid={`feature-feed-${w.slug}`}>
                        {w.is_featured_on_feed ? "★ Featured" : "Feature on feed"}
                      </button>
                      {w.is_suspended ? (
                        <button onClick={() => unsuspend(w.wedding_id)} className="text-xs text-emerald-600 hover:underline" data-testid={`unsuspend-${w.slug}`}>Restore</button>
                      ) : (
                        <button onClick={() => suspend(w.wedding_id)} className="text-xs text-red-500 hover:underline" data-testid={`suspend-${w.slug}`}>Suspend</button>
                      )}
                    </td>
                  </tr>
                ))}
                {!items.length && !busy && (
                  <tr><td colSpan="8" className="py-10 text-center text-[var(--muted)] text-sm">No weddings found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </AdminGate>
  );
}

// ===================== USERS =====================
export function AdminUsersPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const PLANS = ["free", "premium", "luxury", "pro"];

  async function load() {
    setBusy(true);
    try { const { data } = await api.get("/admin/users"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  useEffect(() => { load(); }, []);

  async function setPlan(uid, plan) {
    try {
      await api.patch("/admin/users/plan", { user_id: uid, plan });
      toast.success(`Plan updated → ${plan}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const term = q.toLowerCase();
    return items.filter((u) => (u.email || "").toLowerCase().includes(term) || (u.name || "").toLowerCase().includes(term));
  }, [items, q]);

  return (
    <AdminGate>
      <div data-testid="admin-users">
        <PageHeader title="Users & Plans" subtitle="Search and override subscription tiers." />
        <Card className="mb-6">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email or name…" className="input-luxe pl-10" data-testid="admin-users-search" />
          </div>
        </Card>
        <Card>
          <p className="text-xs text-[var(--muted)] mb-3">{filtered.length} users</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] border-b border-[var(--line)]">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Role</th>
                  <th className="py-2 pr-4">Plan</th>
                  <th className="py-2 pr-2">Set plan</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 200).map((u) => (
                  <tr key={u.user_id} className="border-b border-[var(--line)]/60">
                    <td className="py-3 pr-4">{u.name || "—"}</td>
                    <td className="py-3 pr-4 text-xs">{u.email}</td>
                    <td className="py-3 pr-4 text-xs uppercase">{u.role}</td>
                    <td className="py-3 pr-4 text-xs uppercase font-medium">{u.plan || "free"}</td>
                    <td className="py-3 pr-2">
                      <select onChange={(e) => setPlan(u.user_id, e.target.value)} value={u.plan || "free"} className="text-xs border border-[var(--line)] rounded px-2 py-1" data-testid={`set-plan-${u.email}`}>
                        {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </AdminGate>
  );
}

// ===================== MODERATION =====================
export function AdminModerationPage() {
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("memories");

  async function load() {
    setBusy(true);
    try { const { data } = await api.get("/admin/moderation/queue"); setQ(data); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  useEffect(() => { load(); }, []);

  async function decide(kind, id, action) {
    try {
      await api.post(`/admin/moderation/${kind}/${id}/${action}`, {});
      toast.success(`${kind} ${action}d`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  if (!q) return <div className="text-center py-20"><div className="w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin mx-auto" /></div>;

  const TABS = [
    { v: "memories", l: `Memories (${q.memories.length})` },
    { v: "blessings", l: `Blessings (${q.blessings.length})` },
    { v: "vendors", l: `Vendors (${q.vendors.length})` },
    { v: "announcements", l: `Announcements (${q.announcements.length})` },
  ];

  return (
    <AdminGate>
      <div data-testid="admin-moderation">
        <PageHeader title="Moderation Queue" subtitle="Approve or reject pending content across the platform." action={<button onClick={load} disabled={busy} className="btn-outline-gold text-xs"><RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Refresh</button>} />

        <div className="flex flex-wrap gap-2 mb-6 border-b border-[var(--line)] pb-4">
          {TABS.map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`mod-tab-${t.v}`} className={`text-xs uppercase tracking-[0.16em] px-4 py-2 rounded-full ${tab === t.v ? "bg-[var(--ink)] text-white" : "text-[var(--muted)] hover:text-[var(--gold)]"}`}>{t.l}</button>
          ))}
        </div>

        {tab === "memories" && (
          <div className="grid md:grid-cols-3 gap-4">
            {q.memories.map((m) => (
              <Card key={m.memory_id || m.item_id} data-testid={`mod-mem-${m.memory_id || m.item_id}`}>
                <div className="aspect-square bg-[var(--bg-warm)] rounded-lg overflow-hidden mb-3">
                  {m.content_type?.startsWith("image/") && <img src={`${BACKEND_URL}/api/files/${m.storage_path}`} alt="" className="w-full h-full object-cover" />}
                </div>
                <p className="text-xs text-[var(--muted)] truncate">{m.uploader_name || "Anonymous guest"}</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => decide("memory", m.memory_id || m.item_id, "approve")} className="btn-gold flex-1 text-xs" data-testid={`approve-mem-${m.memory_id || m.item_id}`}><Check className="w-3 h-3" /> Approve</button>
                  <button onClick={() => decide("memory", m.memory_id || m.item_id, "reject")} className="btn-outline-gold text-xs" data-testid={`reject-mem-${m.memory_id || m.item_id}`}><X className="w-3 h-3" /></button>
                </div>
              </Card>
            ))}
            {!q.memories.length && <p className="text-[var(--muted)] text-sm">All memories reviewed. ✨</p>}
          </div>
        )}

        {tab === "blessings" && (
          <div className="space-y-3">
            {q.blessings.map((b) => (
              <Card key={b.blessing_id} className="flex items-center gap-4" data-testid={`mod-bless-${b.blessing_id}`}>
                <Mic className="w-5 h-5 text-[var(--gold)] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{b.guest_name || "Anonymous"}</p>
                  {b.message && <p className="text-xs text-[var(--muted)] truncate">{b.message}</p>}
                </div>
                {b.audio_path && <audio controls src={`${BACKEND_URL}/api/files/${b.audio_path}`} className="h-8 max-w-xs" />}
                <button onClick={() => decide("blessing", b.blessing_id, "approve")} className="btn-gold text-xs" data-testid={`approve-bless-${b.blessing_id}`}><Check className="w-3 h-3" /></button>
                <button onClick={() => decide("blessing", b.blessing_id, "reject")} className="btn-outline-gold text-xs" data-testid={`reject-bless-${b.blessing_id}`}><X className="w-3 h-3" /></button>
              </Card>
            ))}
            {!q.blessings.length && <p className="text-[var(--muted)] text-sm">No blessings pending. ✨</p>}
          </div>
        )}

        {tab === "vendors" && (
          <div className="grid md:grid-cols-2 gap-4">
            {q.vendors.map((v) => (
              <Card key={v.vendor_id} data-testid={`mod-vendor-${v.vendor_id}`}>
                <h3 className="font-serif text-xl mb-1">{v.business_name}</h3>
                <p className="text-xs uppercase tracking-[0.16em] text-[var(--gold)] mb-3">{v.category}</p>
                <p className="text-sm text-[var(--muted)] line-clamp-3 mb-3">{v.description}</p>
                <div className="flex gap-2">
                  <button onClick={() => decide("vendor", v.vendor_id, "approve")} className="btn-gold flex-1 text-xs" data-testid={`approve-vendor-${v.vendor_id}`}><Check className="w-3 h-3" /> Approve</button>
                  <button onClick={() => decide("vendor", v.vendor_id, "reject")} className="btn-outline-gold text-xs" data-testid={`reject-vendor-${v.vendor_id}`}><X className="w-3 h-3" /> Reject</button>
                </div>
              </Card>
            ))}
            {!q.vendors.length && <p className="text-[var(--muted)] text-sm">No vendors awaiting approval. ✨</p>}
          </div>
        )}

        {tab === "announcements" && (
          <div className="space-y-3">
            {q.announcements.map((a) => (
              <Card key={a.announcement_id} data-testid={`mod-ann-${a.announcement_id}`}>
                <div className="flex items-start gap-4">
                  <Megaphone className="w-5 h-5 text-[var(--gold)] mt-1" />
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg">{a.title}</p>
                    <p className="text-sm text-[var(--muted)] line-clamp-2">{a.body}</p>
                  </div>
                  <button onClick={() => decide("announcement", a.announcement_id, "approve")} className="btn-gold text-xs" data-testid={`approve-ann-${a.announcement_id}`}><Check className="w-3 h-3" /> Publish</button>
                  <button onClick={() => decide("announcement", a.announcement_id, "reject")} className="btn-outline-gold text-xs"><X className="w-3 h-3" /></button>
                </div>
              </Card>
            ))}
            {!q.announcements.length && <p className="text-[var(--muted)] text-sm">No announcements pending. ✨</p>}
          </div>
        )}
      </div>
    </AdminGate>
  );
}

// ===================== VENDORS (alias of moderation->vendors) =====================
export function AdminVendorsPage() {
  const [items, setItems] = useState([]);
  async function load() {
    try { const { data } = await api.get("/admin/vendors"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); }, []);

  async function setStatus(vid, status) {
    try {
      await api.patch(`/admin/vendors/${vid}/status`, { status });
      toast.success(`Vendor → ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <AdminGate>
      <div data-testid="admin-vendors">
        <PageHeader title="Vendor Approvals" subtitle="Review and manage every vendor profile." />
        <Card>
          <p className="text-xs text-[var(--muted)] mb-3">{items.length} total · {items.filter((v) => v.status === "pending").length} pending</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] border-b border-[var(--line)]">
                  <th className="py-2 pr-4">Business</th>
                  <th className="py-2 pr-4">Category</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Rating</th>
                  <th className="py-2 pr-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((v) => (
                  <tr key={v.vendor_id} className="border-b border-[var(--line)]/60">
                    <td className="py-3 pr-4 font-medium">{v.business_name}</td>
                    <td className="py-3 pr-4 text-xs uppercase">{v.category}</td>
                    <td className="py-3 pr-4">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                        v.status === "approved" ? "bg-emerald-50 text-emerald-600"
                        : v.status === "pending" ? "bg-amber-50 text-amber-600"
                        : "bg-red-50 text-red-600"
                      }`}>{(v.status || "pending").toUpperCase()}</span>
                    </td>
                    <td className="py-3 pr-4 text-xs">{v.avg_rating ? `★ ${v.avg_rating}` : "—"}</td>
                    <td className="py-3 pr-2 text-right">
                      <select value={v.status || "pending"} onChange={(e) => setStatus(v.vendor_id, e.target.value)} className="text-xs border border-[var(--line)] rounded px-2 py-1" data-testid={`vendor-status-${v.vendor_id}`}>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </td>
                  </tr>
                ))}
                {!items.length && <tr><td colSpan="5" className="py-8 text-center text-[var(--muted)]">No vendors yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </AdminGate>
  );
}

// ===================== REVENUE =====================
export function AdminRevenuePage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);

  async function load() {
    try { const { data } = await api.get(`/admin/revenue/summary?days=${days}`); setData(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="text-center py-20"><div className="w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin mx-auto" /></div>;
  const maxDay = Math.max(1, ...data.daily.map((d) => d.amount_usd));

  return (
    <AdminGate>
      <div data-testid="admin-revenue">
        <PageHeader
          title="Revenue"
          subtitle={`${data.txn_count} paid transactions · last ${days} days`}
          action={
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} className="text-xs border border-[var(--line)] rounded px-3 py-1.5" data-testid="revenue-window">
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
              <option value={365}>1 year</option>
            </select>
          }
        />
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Stat label="Gross (USD)" value={`$${data.gross_usd.toLocaleString()}`} icon={DollarSign} accent="green" testid="rev-gross" />
          <Stat label="Transactions" value={data.txn_count} icon={Activity} testid="rev-count" />
          <Stat label="Avg deal size" value={`$${data.txn_count ? (data.gross_usd / data.txn_count).toFixed(2) : "0"}`} icon={TrendingUp} testid="rev-avg" />
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <Card>
            <h3 className="font-serif text-xl mb-4">By plan</h3>
            {Object.entries(data.by_plan).length ? Object.entries(data.by_plan).map(([plan, amt]) => (
              <Bar key={plan} label={plan} value={amt} total={data.gross_usd} />
            )) : <p className="text-[var(--muted)] text-sm">No revenue yet in this window.</p>}
          </Card>

          <Card>
            <h3 className="font-serif text-xl mb-4">Top customers</h3>
            <div className="space-y-2">
              {data.top_customers.map((c) => (
                <div key={c.user_id} className="flex items-center justify-between text-sm border-b border-[var(--line)]/60 pb-2">
                  <div>
                    <p className="font-medium">{c.name || c.email}</p>
                    <p className="text-xs text-[var(--muted)]">{c.email} · {c.plan || "free"}</p>
                  </div>
                  <p className="font-medium">${c.total_usd.toLocaleString()}</p>
                </div>
              ))}
              {!data.top_customers.length && <p className="text-[var(--muted)] text-sm">No paying customers yet.</p>}
            </div>
          </Card>
        </div>

        <Card>
          <h3 className="font-serif text-xl mb-4">Daily series</h3>
          {data.daily.length ? (
            <div className="flex items-end gap-1 h-40" data-testid="rev-daily-chart">
              {data.daily.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                  <div title={`${d.date} · $${d.amount_usd}`} className="w-full bg-[var(--gold)] rounded-t" style={{ height: `${(d.amount_usd / maxDay) * 100}%`, minHeight: d.amount_usd ? 2 : 0 }} />
                </div>
              ))}
            </div>
          ) : <p className="text-[var(--muted)] text-sm">No data in this window.</p>}
        </Card>
      </div>
    </AdminGate>
  );
}

// ===================== REPORTS =====================
export function AdminReportsPage() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("open");

  async function load() {
    try { const { data } = await api.get(`/admin/reports?status=${status}`); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  async function resolve(id, action) {
    try {
      await api.post(`/admin/reports/${id}/resolve`, { action, note: "" });
      toast.success("Report resolved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <AdminGate>
      <div data-testid="admin-reports">
        <PageHeader title="Content Reports" subtitle="User-submitted flags on content across the platform." action={
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="text-xs border border-[var(--line)] rounded px-3 py-1.5" data-testid="reports-status">
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
            <option value="all">All</option>
          </select>
        } />
        <Card>
          <div className="space-y-3">
            {items.map((r) => (
              <div key={r.report_id} className="flex items-start gap-4 border-b border-[var(--line)]/60 pb-3" data-testid={`report-${r.report_id}`}>
                <FlagTriangleRight className="w-5 h-5 text-red-500 mt-1" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm">
                    <span className="font-medium uppercase text-xs tracking-[0.16em] text-[var(--gold)]">{r.target_kind}</span>
                    <span className="text-[var(--muted)] mx-2">·</span>
                    <span className="text-xs text-[var(--muted)]">{r.target_id}</span>
                  </p>
                  <p className="text-sm mt-1">{r.reason}</p>
                  {r.reporter_email && <p className="text-xs text-[var(--muted)] mt-0.5">From: {r.reporter_email}</p>}
                </div>
                {r.status === "open" ? (
                  <div className="flex gap-2">
                    <button onClick={() => resolve(r.report_id, "removed")} className="btn-gold text-xs" data-testid={`resolve-removed-${r.report_id}`}>Remove</button>
                    <button onClick={() => resolve(r.report_id, "dismiss")} className="btn-outline-gold text-xs" data-testid={`resolve-dismiss-${r.report_id}`}>Dismiss</button>
                  </div>
                ) : (
                  <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-600">Resolved · {r.resolved_action}</span>
                )}
              </div>
            ))}
            {!items.length && <p className="text-[var(--muted)] text-sm py-6 text-center">No {status} reports. ✨</p>}
          </div>
        </Card>
      </div>
    </AdminGate>
  );
}

// ===================== BROADCAST =====================
export function AdminBroadcastPage() {
  const [channel, setChannel] = useState("email");
  const [subject, setSubject] = useState("");
  const [subjectB, setSubjectB] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [smsBody, setSmsBody] = useState("");
  const [audience, setAudience] = useState("all");
  const [scheduledAt, setScheduledAt] = useState(""); // local datetime input
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const [filter, setFilter] = useState("all");

  async function loadRecent() {
    try { const { data } = await api.get(`/admin/broadcasts?status=${filter}`); setRecent(data); }
    catch {}
  }
  useEffect(() => { loadRecent(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  function isoFromLocal(local) {
    if (!local) return null;
    // <input type="datetime-local"> gives "YYYY-MM-DDTHH:mm" in *local* tz.
    // Convert to ISO with the browser's TZ offset baked in.
    const d = new Date(local);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  }

  async function send() {
    if (channel === "email") {
      if (!subject.trim() || !bodyHtml.trim()) return toast.error("Subject and body required");
    } else {
      if (!smsBody.trim()) return toast.error("Message required");
    }
    const scheduledIso = isoFromLocal(scheduledAt);
    const verb = scheduledIso ? "schedule" : "send";
    if (!confirm(`${verb === "schedule" ? "Schedule" : "Send"} this ${channel} to ${audience}? This is irreversible.`)) return;

    setBusy(true);
    try {
      const url = channel === "email" ? "/admin/broadcast/email" : "/admin/broadcast/sms";
      const payload = channel === "email"
        ? {
            subject, body_html: bodyHtml, audience,
            ...(subjectB.trim() ? { subject_b: subjectB.trim() } : {}),
            ...(scheduledIso ? { scheduled_at: scheduledIso } : {}),
          }
        : {
            message: smsBody, audience,
            ...(scheduledIso ? { scheduled_at: scheduledIso } : {}),
          };
      const { data } = await api.post(url, payload);
      if (data.status === "scheduled") {
        toast.success(`Scheduled for ${new Date(data.scheduled_at).toLocaleString()}`);
      } else {
        toast.success(`${data.recipients} recipients · ${data.mocked ? "MOCKED (no key)" : `sent ${data.sent}`}${data.ab_test ? " · A/B test" : ""}`);
      }
      setSubject(""); setSubjectB(""); setBodyHtml(""); setSmsBody(""); setScheduledAt("");
      loadRecent();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function cancel(bid) {
    if (!confirm("Cancel this scheduled broadcast?")) return;
    try {
      await api.post(`/admin/broadcast/${bid}/cancel`);
      toast.success("Broadcast cancelled");
      loadRecent();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function runNow(bid) {
    if (!confirm("Send this scheduled broadcast NOW?")) return;
    try {
      const { data } = await api.post(`/admin/broadcast/${bid}/run-now`);
      toast.success(`Dispatched · ${data.sent} sent / ${data.recipients} recipients`);
      loadRecent();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <AdminGate>
      <div data-testid="admin-broadcast">
        <PageHeader title="Broadcast" subtitle="Send platform-wide email or SMS — schedule for later or split-test subject lines." />

        <Card className="mb-6">
          <div className="flex gap-2 mb-5">
            <button onClick={() => setChannel("email")} data-testid="bc-channel-email" className={`text-xs uppercase tracking-[0.16em] px-4 py-2 rounded-full ${channel === "email" ? "bg-[var(--ink)] text-white" : "text-[var(--muted)]"}`}>Email</button>
            <button onClick={() => setChannel("sms")} data-testid="bc-channel-sms" className={`text-xs uppercase tracking-[0.16em] px-4 py-2 rounded-full ${channel === "sms" ? "bg-[var(--ink)] text-white" : "text-[var(--muted)]"}`}>SMS</button>
          </div>

          <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Audience</label>
          <select value={audience} onChange={(e) => setAudience(e.target.value)} className="input-luxe mb-4" data-testid="bc-audience">
            <option value="all">All users</option>
            <option value="couples">Couples only</option>
            <option value="vendors">Vendors only</option>
            <option value="planners">Planners only</option>
            {channel === "email" && <option value="admins">Admins only</option>}
          </select>

          {channel === "email" ? (
            <>
              <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Subject A</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} className="input-luxe mb-3" data-testid="bc-subject" placeholder="A new chapter for LockedInForever…" />
              <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] flex items-center gap-2">
                Subject B <span className="text-[10px] text-[var(--gold)]">A/B test (optional — splits audience 50/50)</span>
              </label>
              <input value={subjectB} onChange={(e) => setSubjectB(e.target.value)} className="input-luxe mb-4" data-testid="bc-subject-b" placeholder="Leave blank to skip A/B test" />
              <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Body (HTML supported)</label>
              <textarea value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} rows={8} className="input-luxe resize-none font-mono text-xs" data-testid="bc-body" placeholder="<p>Hello couples,</p>…" />
            </>
          ) : (
            <>
              <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Message ({smsBody.length}/480)</label>
              <textarea value={smsBody} onChange={(e) => setSmsBody(e.target.value.slice(0, 480))} rows={4} className="input-luxe resize-none" data-testid="bc-sms" placeholder="Big news from LockedInForever…" />
            </>
          )}

          <div className="mt-4">
            <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] flex items-center gap-2">
              Schedule for later <span className="text-[10px] text-[var(--gold)]">(optional — leave blank to send immediately)</span>
            </label>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              data-testid="bc-scheduled-at"
              className="input-luxe"
            />
          </div>

          <button onClick={send} disabled={busy} data-testid="bc-send" className="btn-gold mt-5">
            <Send className="w-4 h-4" /> {busy ? "Working…" : (scheduledAt ? `Schedule for ${audience}` : `Send to ${audience}`)}
          </button>
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-xl">Recent broadcasts</h3>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="text-xs border border-[var(--line)] rounded px-3 py-1.5" data-testid="bc-filter">
              <option value="all">All</option>
              <option value="scheduled">Scheduled</option>
              <option value="sent">Sent</option>
              <option value="mocked">Mocked</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="space-y-2">
            {recent.map((b) => (
              <div key={b.broadcast_id} className="flex items-start gap-3 border-b border-[var(--line)]/60 pb-2 text-sm" data-testid={`broadcast-row-${b.broadcast_id}`}>
                <span className={`text-[10px] uppercase tracking-[0.18em] mt-1 px-2 py-0.5 rounded-full ${
                  b.status === "scheduled" ? "bg-amber-50 text-amber-600"
                  : b.status === "sent" ? "bg-emerald-50 text-emerald-600"
                  : b.status === "cancelled" ? "bg-red-50 text-red-600"
                  : "bg-[var(--bg-warm)] text-[var(--muted)]"
                }`}>{b.status || "pending"}</span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--gold)] mt-1">{b.channel}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {b.subject || (b.message || "").slice(0, 80)}
                    {b.subject_b && <span className="text-xs text-[var(--gold)] ml-2">⟷ A/B</span>}
                  </p>
                  <p className="text-xs text-[var(--muted)]">
                    {b.audience} ·{" "}
                    {b.status === "scheduled"
                      ? `scheduled for ${new Date(b.scheduled_at).toLocaleString()}`
                      : `${b.recipient_count || 0} recipients · ${b.mocked ? "mocked" : `sent ${b.sent}`}`}
                    {b.subject_b && b.subject_a_count > 0 && ` · A:${b.subject_a_count} / B:${b.subject_b_count}`}
                  </p>
                </div>
                {b.status === "scheduled" && (
                  <>
                    <button onClick={() => runNow(b.broadcast_id)} className="text-xs text-[var(--gold)] hover:underline" data-testid={`bc-run-now-${b.broadcast_id}`}>Send now</button>
                    <button onClick={() => cancel(b.broadcast_id)} className="text-xs text-red-500 hover:underline" data-testid={`bc-cancel-${b.broadcast_id}`}>Cancel</button>
                  </>
                )}
                <span className="text-xs text-[var(--muted)] whitespace-nowrap">{(b.created_at || "").slice(0, 10)}</span>
              </div>
            ))}
            {!recent.length && <p className="text-[var(--muted)] text-sm">No broadcasts in this view.</p>}
          </div>
        </Card>
      </div>
    </AdminGate>
  );
}

// ===================== AUDIT LOG =====================
export function AdminAuditLogPage() {
  const [rows, setRows] = useState([]);
  async function load() {
    try { const { data } = await api.get("/admin/audit"); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); }, []);

  return (
    <AdminGate>
      <div data-testid="admin-audit">
        <PageHeader title="Audit Log" subtitle="Every admin action, with actor + timestamp." />
        <Card>
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.audit_id} className="flex items-start gap-4 border-b border-[var(--line)]/60 pb-2 text-sm">
                <ScrollText className="w-4 h-4 text-[var(--gold)] mt-1" />
                <div className="flex-1 min-w-0">
                  <p>
                    <span className="font-medium">{r.actor_email}</span>
                    <span className="text-[var(--muted)]"> · </span>
                    <span className="uppercase text-xs tracking-[0.16em] text-[var(--gold)]">{r.action}</span>
                    <span className="text-[var(--muted)]"> · </span>
                    <span className="text-xs">{r.target_kind}/{r.target_id}</span>
                  </p>
                  {r.meta && Object.keys(r.meta).length > 0 && (
                    <p className="text-xs text-[var(--muted)] mt-0.5">{JSON.stringify(r.meta)}</p>
                  )}
                </div>
                <span className="text-xs text-[var(--muted)] whitespace-nowrap">{(r.created_at || "").replace("T", " ").slice(0, 16)}</span>
              </div>
            ))}
            {!rows.length && <p className="text-[var(--muted)] text-sm py-6 text-center">No admin actions logged yet.</p>}
          </div>
        </Card>
      </div>
    </AdminGate>
  );
}
